import { GalleryWithProgress } from '@divlab/divanui';
import { Children, useCallback, useState } from 'react';

import type { FC, MouseEvent, ReactElement } from 'react';
import type { ProgressOptions, GalleryWithProgressProps } from '@divlab/divanui';

export interface RenderButtonsArgs {
  onPrev?: (e: MouseEvent) => void;
  onNext?: (e: MouseEvent) => void;
}

export type RenderButtons = (args: RenderButtonsArgs) => ReactElement;

export interface GalleryWithArrowsProps extends GalleryWithProgressProps {
  cnContainer?: string;
  renderButtons?: RenderButtons;
}

const GalleryWithButtons: FC<GalleryWithArrowsProps> = (props) => {
  const { children, renderButtons, cnContainer, ...restProps } = props;
  const elementsCount = Children.count(children);
  const [slide, setSlide] = useState(0);
  const [track, setTrack] = useState<ProgressOptions>(null);

  const normalizeSlide = useCallback(
    (value: number) => {
      if (value < 0) return 0;
      if (value > elementsCount) return elementsCount;

      return value;
    },
    [elementsCount],
  );

  const handleChangeCurrent = useCallback(({ current }) => {
    setSlide(current);
  }, []);

  const handleChangeProgress = useCallback((opts: ProgressOptions) => {
    setTrack(opts);
  }, []);

  const handlePrev = useCallback(() => {
    setSlide((prev) => normalizeSlide(prev - 1));
  }, [normalizeSlide]);

  const handleNext = useCallback(() => {
    if (track.finished) return;

    setSlide((prev) => normalizeSlide(prev + 1));
  }, [normalizeSlide, track]);

  return (
    <div className={cnContainer}>
      {renderButtons && renderButtons({ onPrev: handlePrev, onNext: handleNext })}
      <GalleryWithProgress
        {...restProps}
        slideIndex={slide}
        onChangeCurrent={handleChangeCurrent}
        onChangeProgress={handleChangeProgress}
        key={elementsCount}
      >
        {children}
      </GalleryWithProgress>
    </div>
  );
};

export default GalleryWithButtons;
